<template>
  <Modal
    v-if="item.tempCommand"
    :value="value"
    @hide="hide()"
    :title="item.chocoPackage.Title"
    :subtitle="`${item.chocoPackage.Id} - ${nameAndVersion.version}`"
  >
    <template slot="body" class="content">
      <div v-if="isPseudo" class="notification is-warning">
        This package does not exist in the repo it originated from, therefore
        only uninstallation and delete from database is possible. Uninstallation
        is needed before deletion, since deletion would remove the abillity to
        controll the package.
      </div>

      <Warnings v-if="installable" :item="item"></Warnings>

      <div v-if="group" class="notification is-info">
        This package is managed by the group
        <router-link
          :to="
            `/domains/${$route.params.domainUID}/groups/${group.uid}/packages`
          "
        >
          <span class="is-italic">{{ group.name }}</span> </router-link
        >. Overriding a package managed by a group can lead to unwanted side
        effects.
        <span v-if="autoUpgrade">
          The package will auto upgrade to the latest version according to the
          group package schedule.
        </span>
      </div>

      <div v-if="!group && autoUpgrade" class="notification is-primary">
        This package is managed by the group
        <router-link
          :to="
            `/domains/${$route.params.domainUID}/groups/${autoUpgrade.uid}/packages`
          "
        >
          <span class="is-italic">{{ autoUpgrade.name }}</span> </router-link
        >. The package will auto upgrade to the latest version according to the
        group package schedule.
      </div>

      <Switches :item="item"></Switches>
      <Parameters :item="item"></Parameters>
    </template>
    <template slot="footer" class="content">
      <div v-show="!isLoading">
        <div
          v-if="upgradable && !hasParam('-m')"
          :disabled="
            ((hasMultiple || higerVersionInstalled) && !hasParam('-m')) ||
              working ||
              isPseudo
          "
          class="button is-info"
          @click="changePackage(item, 'upgrade')"
        >
          Upgrade
        </div>

        <div
          v-if="
            !isPseudo &&
              ((installable && !upgradable) || (upgradable && hasParam('-m')))
          "
          :disabled="
            ((hasMultiple || higerVersionInstalled) && !hasParam('-m')) ||
              working ||
              isPseudo
          "
          class="button is-success"
          @click="changePackage(item, 'install')"
        >
          Install
        </div>

        <div
          v-if="uninstallable"
          :disabled="!uninstallable || working"
          class="button is-danger"
          @click="changePackage(item, 'uninstall')"
        >
          Uninstall
        </div>

        <div
          v-if="isPseudo"
          :disabled="!uninstalled"
          class="button is-warning"
          @click="uninstalled ? deletePackage() : false"
          data-tooltip="Remove from database"
        >
          Delete
        </div>

        <div class="button" @click="hide()">Cancel</div>
      </div>
      <div v-show="isLoading" class="spinner-wrapper center">
        <span class="spinner is-centered"></span>
      </div>
    </template>
  </Modal>
</template>
<script>
import { getPackageNameVersionAndHash, hasParam, getGroup } from "@/utils/mdc";
import {
  formatPackage,
  addTempParam,
  getAllMdcVersions,
  getAllMdcVersionsFn,
} from "@/utils/packages";

import status from "@/utils/packageStatus";

import { sendPackages } from "@/utils/socket";

import { postApi } from "@/utils/http";

export default {
  props: { value: Boolean, item: Object },
  components: {
    Warnings: () => import("@/components/main/packages/Warnings.vue"),
    Switches: () => import("@/components/main/packages/Switches.vue"),
    Parameters: () => import("@/components/main/packages/Parameters.vue"),
    Modal: () => import("./Modal"),
  },
  computed: {
    hasParam() {
      return (param) => {
        return hasParam(this.item.tempCommand.parameters, param);
      };
    },
    group() {
      return getGroup(this.item);
    },

    isPseudo() {
      return this.item?.chocoPackage?.Pseudo;
    },
    higerVersionInstalled() {
      return status.isHigerVersionInstalled(this.item);
    },
    autoUpgrade() {
      return status.hasAutoUpgrade(this.item);
    },
    hasMultiple() {
      return status.hasMultipleVersions(this.item);
    },
    working() {
      return status.isWorking(this.item);
    },
    upgradable() {
      return status.isUpgradable(this.item);
    },
    installable() {
      return status.isInstallable(this.item);
    },
    uninstallable() {
      return status.isUninstallable(this.item);
    },
    uninstalled() {
      return status.isUninstallDone(this.item);
    },
    nameAndVersion() {
      return getPackageNameVersionAndHash(this.item);
    },
  },
  data() {
    return { isLoading: false };
  },
  async created() {
    this.isLoading = true;

    if (this.item) {
      const { name, uid } = this.nameAndVersion;
      const shouldReload = !this.$store.state.computerPackage
        .fullyLoadedPackages[name + uid];

      if (shouldReload) {
        const mdcPackages = await getAllMdcVersions(
          this.item,
          "computerPackage",
          getAllMdcVersionsFn(this.item, false, this.$route.params)
        );

        this.$store.commit(
          `computerPackage/setMergedPackageVersions`,
          mdcPackages
        );
      }

      this.$store.commit("computerPackage/editMergedPackage", {
        p: this.item,
        obj: { tempCommand: { ...this.item.command } },
      });
    }

    this.isLoading = false;
  },
  methods: {
    validate(action) {
      if (
        (action === "upgrade" &&
          (this.hasMultiple || this.higerVersionInstalled) &&
          !this.hasParam("-m")) ||
        this.working
      )
        return false;
      else if (
        action === "install" &&
        (((this.hasMultiple || this.higerVersionInstalled) &&
          !this.hasParam("-m")) ||
          this.working)
      )
        return false;
      else if (action === "uninstall" && (!this.uninstallable || this.working))
        return false;

      return true;
    },
    changePackage(item, action) {
      if (this.validate(action)) {
        this.isLoading = true;
        this.$emit("action", action);

        const newItem = formatPackage(item, action);
        newItem.command.override = true;

        if (
          sendPackages(
            [newItem],
            [item.computerUID || this.$route.params.computerUID]
          )
        ) {
          this.$store.commit("computerPackage/mergeMdcPackage", {
            ...item,
            command: newItem.command,
          });

          this.$store.commit("computerPackage/editMergedPackage", {
            p: item,
            obj: { command: newItem.command },
          });
        } else {
          this.$helpers.notify(
            "Error",
            "Could not send the package.",
            "danger"
          );
        }

        if (newItem.command.streamLog) this.$emit("openActionInfo");
        else this.hide();
      }
    },
    async deletePackage() {
      try {
        this.isLoading = true;

        const { name, version, hash } = getPackageNameVersionAndHash(this.item);

        await postApi("delete/computerPackage", {
          packageHash: hash,
          packageName: name,
          domainUID: this.$route.params.domainUID,
          sourceUID: this.item.source.uid,
          computerUIDs: [
            this.item.computerUID || this.$route.params.computerUID,
          ],
        });

        this.$store.commit("computerPackage/removeMergedPackage", this.item);
      } catch (error) {
        console.error(error);
        this.$helpers.notify(
          "Error",
          "Could not delete the package.",
          "danger"
        );
      }

      this.hide();
    },
    hide() {
      this.$store.commit("computerPackage/editMergedPackage", {
        p: this.item,
        obj: { tempCommand: null },
      });

      this.$emit("hide");
      this.$emit("input", false);
    },
  },
};
</script>
